var _excluded = ["name", "heading", "children", "onSubmit", "submitDisabled", "submitText", "submitKind", "onClose", "closeText", "closeKind", "standalone", "focusChildElement", "renderTo", "headerElement", "footerElement", "size", "className"];
function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useContext, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Context } from './ModalDialogProvider';
import FocusTrap from '../../atoms/FocusTrap/FocusTrap';
import Button from '../../atoms/Button/Button';
var KEY_ESC = 27;

/**
 * Status: *in progress*.
 * Category: Notifications
 *
 * Look at the <a href='#/modal-dialog'>Modal Dialog sample page</a> to see this component in action.
 *
 * TODO:
 ** &lt;div role="dialog"&gt; can be changed to &lt;dialog&gt; when this element is more widely supported
 */

function ModalDialog(_ref) {
  var name = _ref.name,
    heading = _ref.heading,
    children = _ref.children,
    onSubmit = _ref.onSubmit,
    submitDisabled = _ref.submitDisabled,
    submitText = _ref.submitText,
    submitKind = _ref.submitKind,
    onClose = _ref.onClose,
    closeText = _ref.closeText,
    closeKind = _ref.closeKind,
    standalone = _ref.standalone,
    focusChildElement = _ref.focusChildElement,
    renderTo = _ref.renderTo,
    headerElement = _ref.headerElement,
    footerElement = _ref.footerElement,
    size = _ref.size,
    className = _ref.className,
    rest = _objectWithoutProperties(_ref, _excluded);
  var modalNode = renderTo || useContext(Context);
  var dialogRef = useRef();
  var dialogOverlayRef = useRef();
  var returnFocusOnDialogClose = function returnFocusOnDialogClose() {
    if (standalone) return;
    var lastFocusedElement = document.activeElement;
    return function () {
      if (lastFocusedElement) lastFocusedElement.focus();
    };
  };
  var setFocusOnDialog = function setFocusOnDialog() {
    if (!dialogRef.current || standalone || focusChildElement) return;
    dialogRef.current.focus();
  };
  var disableAndResetPageScroll = function disableAndResetPageScroll() {
    if (standalone) return;
    var previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (dialogOverlayRef.current) {
      dialogOverlayRef.current.scrollTop = 0;
    }
    return function () {
      document.body.style.overflow = previousOverflow;
    };
  };
  var onOverlayClick = function onOverlayClick(event) {
    if (event.target !== dialogOverlayRef.current) return;
    if (onClose) onClose();
  };
  var handleKeyDown = function handleKeyDown(event) {
    if (event.keyCode === KEY_ESC && onClose) onClose();
  };
  useEffect(returnFocusOnDialogClose, []);
  useEffect(setFocusOnDialog, [dialogRef.current]);
  useEffect(disableAndResetPageScroll, []);
  var defaultHeaderElement = /*#__PURE__*/React.createElement("h2", {
    id: "".concat(name, "-heading"),
    className: "modal-dialog__heading"
  }, heading);
  var defaultFooterElement = /*#__PURE__*/React.createElement("div", {
    className: "modal-dialog__footer"
  }, closeText && onClose && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    kind: closeKind,
    margin: "top",
    onClick: onClose,
    text: closeText
  }), submitText && onSubmit && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    kind: submitKind,
    margin: "top",
    onClick: onSubmit,
    text: submitText,
    isDisabled: submitDisabled
  }));
  var renderDialog = function renderDialog(Component) {
    var additionalProps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return /*#__PURE__*/React.createElement(Component, _extends({
      tabIndex: 0,
      onKeyDown: handleKeyDown,
      ref: dialogRef,
      className: classnames('modal-dialog container', "container--".concat(size), {
        'modal-dialog--standalone': standalone
      }, className ? className : undefined),
      role: "dialog",
      "aria-labelledby": "".concat(name, "-heading"),
      "aria-describedby": "".concat(name, "-description")
    }, rest, additionalProps), headerElement || defaultHeaderElement, /*#__PURE__*/React.createElement("section", {
      id: "".concat(name, "-description")
    }, children), footerElement || defaultFooterElement);
  };
  if (standalone) {
    return renderDialog('div');
  }
  if (!modalNode) return null;

  // portals need to be wrapped in a fragment because react-docgen doesn't recognize them
  // as a component ref: https://github.com/reactjs/react-docgen/issues/336
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    ref: dialogOverlayRef,
    onClick: onOverlayClick,
    tabIndex: "-1",
    className: "modal-dialog-overlay"
  }, renderDialog(FocusTrap, {
    as: 'div'
  }))), modalNode));
}
ModalDialog.sizes = {
  small: 'small',
  medium: 'medium',
  large: 'large'
};
ModalDialog.defaultProps = {
  closeKind: Button.kinds.cancel,
  size: ModalDialog.sizes.small
};
ModalDialog.propTypes = {
  name: PropTypes.string.isRequired,
  heading: PropTypes.string,
  children: PropTypes.node,
  onSubmit: PropTypes.func,
  submitDisabled: PropTypes.bool,
  submitText: PropTypes.string,
  submitKind: PropTypes.oneOf(Object.values(Button.kinds)),
  onClose: PropTypes.func,
  closeText: PropTypes.string,
  closeKind: PropTypes.oneOf(Object.values(Button.kinds)),
  standalone: PropTypes.bool,
  renderTo: PropTypes.any,
  headerElement: PropTypes.element,
  footerElement: PropTypes.element,
  size: PropTypes.oneOf(Object.keys(ModalDialog.sizes))
};
export default ModalDialog;